// src/pages/league-dashboard/salary-contract/components/PositionScarcityModal.js
//
// Extracted from inline definition in PricingLogicTab.js per Tier 2 #6
// commit 6a (refactor only — no behavior change).
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { RotateCcw } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';
import { parseFloatOrPrev } from '../../../../services/formUtils';
import { DEFAULT_PRICING_LOGIC } from '../tabs/PricingLogicTab';

// =============================================================================
// POSITION SCARCITY MODAL COMPONENT
// =============================================================================
const PositionScarcityModal = ({ isOpen, onClose, positionScarcity, onChange }) => {
  const [localValues, setLocalValues] = useState({});

  useEffect(() => {
    if (isOpen) {
      // Intentional `||` (not `??`): empty {} positionScarcity should fall through
      // to canonical DEFAULT; ?? would preserve {} and break the no-positions-
      // configured fallback. Per 8m.6c audit Section F.4 / G.2 resolution.
      setLocalValues(positionScarcity || DEFAULT_PRICING_LOGIC.position_scarcity);
    }
  }, [isOpen, positionScarcity]);

  if (!isOpen) return null;

  const positions = [
    { key: 'C', label: 'Catcher', description: 'Very scarce - few quality options' },
    { key: '1B', label: 'First Base', description: 'Abundant position' },
    { key: '2B', label: 'Second Base', description: 'Moderate scarcity' },
    { key: '3B', label: 'Third Base', description: 'Abundant position' },
    { key: 'SS', label: 'Shortstop', description: 'Moderate scarcity' },
    { key: 'OF', label: 'Outfield', description: 'Most abundant position' },
    { key: 'SP', label: 'Starting Pitcher', description: 'Standard availability' },
    { key: 'RP', label: 'Relief Pitcher', description: 'Standard availability' },
    { key: 'CP', label: 'Closer', description: 'High scarcity - role based' }
  ];

  const handleSave = () => {
    onChange(localValues);
    onClose();
  };

  const handleReset = () => {
    setLocalValues(DEFAULT_PRICING_LOGIC.position_scarcity);
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
      style={{ zIndex: 999999 }}
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-lg ${dynastyTheme.components.card.base} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>
            Edit Position Scarcity Multipliers
          </h3>
          <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-6`}>
            Adjust multipliers to boost prices for scarce positions. Applied after z-score calculation.
          </p>

          <div className="space-y-4">
            {positions.map(({ key, label, description }) => (
              <div key={key} className={`${dynastyTheme.classes.bg.darkLighter} p-3 rounded-md`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className={`${dynastyTheme.classes.text.white} font-medium`}>{key}</span>
                    <span className={`${dynastyTheme.classes.text.neutralLight} text-sm ml-2`}>({label})</span>
                  </div>
                  <span className={`${dynastyTheme.classes.text.primary} font-bold`}>
                    {(localValues[key] ?? 1.0).toFixed(2)}×
                  </span>
                </div>
                <input
                  type="range"
                  min={1.0}
                  max={1.5}
                  step={0.05}
                  value={localValues[key] ?? 1.0}
                  onChange={(e) => setLocalValues({
                    ...localValues,
                    [key]: parseFloatOrPrev(e.target.value, localValues[key])
                  })}
                  className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-yellow-400"
                />
                <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
                  {description}
                </p>
                {localValues[key] > 1.3 && (
                  <p className={`${dynastyTheme.classes.text.warning} text-xs mt-1`}>
                    ⚠️ High multiplier - may overprice this position
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-6">
            <button
              onClick={handleReset}
              className={dynastyTheme.utils.getComponent('button', 'ghost', 'sm')}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset to Defaults
            </button>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className={dynastyTheme.utils.getComponent('button', 'primary', 'sm')}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default PositionScarcityModal;
